import React, { useEffect, useState } from "react";
import {
  fetchFinanceZReport,
  fetchRoomsReport,
  fetchSummary,
  fetchServicesSummary,
  getCurrency,
  hasFeature,
  getVenueType,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// Business overview (owner / manager) — a balanced, cross-department cockpit
// that puts ROOMS, F&B and WELLNESS side by side. The rest of the analytics
// suite is F&B-heavy; this page pulls the three revenue engines together so
// Nigat / the owner can read the whole property at a glance.
//
// Pure frontend assembly over existing endpoints — no backend change:
//   Revenue by area  ->  /finance-reports/z-report  (manager | cashier)
//   Rooms KPIs       ->  /reservations/report        (manager | controller)
//   F&B KPIs         ->  /reports/summary            (manager, feat=reports)
//   Wellness KPIs    ->  /services/summary           (manager | controller)
// Every section fetches independently and falls back to "—" on failure so a
// single endpoint (role / feature gated) never breaks the whole page.
const S = {
  en: {
    title: "Business overview",
    subtitle: "Rooms, food & beverage and wellness — the whole property at a glance",
    timeframe: "Timeframe",
    tf_today: "Today", tf_week: "This week", tf_month: "This month", tf_year: "This year", tf_custom: "Custom",
    from_l: "From", to_l: "To", range_l: "Range",
    // Section 1
    rev_by_area: "Revenue by area",
    total_revenue: "Total revenue",
    pct_of_total: "of total",
    no_revenue: "No revenue recorded in this range.",
    // Section 2
    rooms: "Rooms",
    occupancy: "Occupancy", adr: "ADR", revpar: "RevPAR",
    room_revenue: "Room revenue", room_nights: "Room-nights", bookings: "Bookings",
    // Section 3
    fnb: "Food & Beverage",
    fnb_revenue: "F&B revenue", orders: "Orders", items_sold: "Items sold", avg_order: "Avg order value",
    // Section 4
    wellness: "Wellness",
    service_revenue: "Service revenue", membership_dues: "Membership dues",
    active_members: "Active members", service_bookings: "Bookings",
    unavailable: "Not available for this account.",
    // area labels
    a_accommodation: "Rooms", a_fnb: "Food & Beverage", a_bar: "Bar",
    a_roomservice: "Room service", a_wellness: "Spa / Wellness", a_events: "Events", a_other: "Other",
  },
  am: {
    title: "የንግድ አጠቃላይ እይታ",                                   // TODO verify
    subtitle: "ክፍሎች፣ ምግብና መጠጥ እና ጤና — ሙሉውን ንብረት በአንድ እይታ",   // TODO verify
    timeframe: "የጊዜ ገደብ",
    tf_today: "ዛሬ", tf_week: "ይህ ሳምንት", tf_month: "ይህ ወር", tf_year: "ይህ ዓመት", tf_custom: "ብጁ",
    from_l: "ከ", to_l: "እስከ", range_l: "ክልል",
    rev_by_area: "ገቢ በአካባቢ",                                    // TODO verify
    total_revenue: "ጠቅላላ ገቢ",
    pct_of_total: "ከጠቅላላው",                                     // TODO verify
    no_revenue: "በዚህ ክልል ውስጥ የተመዘገበ ገቢ የለም።",                 // TODO verify
    rooms: "ክፍሎች",
    occupancy: "ተሞልቶነት", adr: "ADR", revpar: "RevPAR",
    room_revenue: "የክፍል ገቢ", room_nights: "የክፍል ሌሊቶች", bookings: "ማስያዣዎች",
    fnb: "ምግብና መጠጥ",
    fnb_revenue: "የምግብና መጠጥ ገቢ", orders: "ትዕዛዞች", items_sold: "የተሸጡ እቃዎች", avg_order: "አማካይ የትዕዛዝ ዋጋ", // TODO verify
    wellness: "ጤና",
    service_revenue: "የአገልግሎት ገቢ", membership_dues: "የአባልነት ክፍያ",   // TODO verify
    active_members: "ንቁ አባላት", service_bookings: "ማስያዣዎች",
    unavailable: "ለዚህ መለያ አይገኝም።",                               // TODO verify
    a_accommodation: "ክፍሎች", a_fnb: "ምግብና መጠጥ", a_bar: "ባር",       // TODO verify
    a_roomservice: "የክፍል አገልግሎት", a_wellness: "ስፓ / ጤና", a_events: "ዝግጅቶች", a_other: "ሌላ", // TODO verify
  },
  om: {
    title: "Ilaalcha daldalaa",                                  // TODO verify
    subtitle: "Kutaalee, nyaataa fi dhugaatii fi fayyaa — qabeenya guutuu ilaalcha tokkoon", // TODO verify
    timeframe: "Yeroo",
    tf_today: "Har'a", tf_week: "Torban kana", tf_month: "Ji'a kana", tf_year: "Waggaa kana", tf_custom: "Kan mataa",
    from_l: "Irraa", to_l: "Hanga", range_l: "Daangaa",
    rev_by_area: "Galii naannoon",                               // TODO verify
    total_revenue: "Galii waliigalaa",
    pct_of_total: "waliigala irraa",                             // TODO verify
    no_revenue: "Hangii kana keessatti galiin hin galmoofne.",   // TODO verify
    rooms: "Kutaalee",
    occupancy: "Guutuu", adr: "ADR", revpar: "RevPAR",
    room_revenue: "Galii kutaa", room_nights: "Halkanoota kutaa", bookings: "Qabannoo",
    fnb: "Nyaataa fi Dhugaatii",
    fnb_revenue: "Galii nyaataa fi dhugaatii", orders: "Ajajawwan", items_sold: "Meeshaalee gurguraman", avg_order: "Giddu-galeessa gatii ajajaa", // TODO verify
    wellness: "Fayyaa",
    service_revenue: "Galii tajaajilaa", membership_dues: "Kaffaltii miseensummaa",  // TODO verify
    active_members: "Miseensota sochii", service_bookings: "Qabannoo",
    unavailable: "Herrega kanaaf hin argamu.",                   // TODO verify
    a_accommodation: "Kutaalee", a_fnb: "Nyaataa fi Dhugaatii", a_bar: "Baar",   // TODO verify
    a_roomservice: "Tajaajila kutaa", a_wellness: "Spa / Fayyaa", a_events: "Taatewwan", a_other: "Kan biraa", // TODO verify
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };

// --- date helpers (mirrors FinanceReportsPage preset math) -----------------
const iso = (off) => { const d = new Date(); d.setDate(d.getDate() + off); return d.toISOString().slice(0, 10); };
// Monday of the current week (Mon → Sun; Mon = 0).
const mondayIso = () => { const d = new Date(); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() - dow); return d.toISOString().slice(0, 10); };
const firstOfMonthIso = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };
const firstOfYearIso = () => `${new Date().getFullYear()}-01-01`;
const presetRange = (preset, cFrom, cTo) => {
  const today = iso(0);
  switch (preset) {
    case "week": return { from: mondayIso(), to: today };
    case "month": return { from: firstOfMonthIso(), to: today };
    case "year": return { from: firstOfYearIso(), to: today };
    case "custom": return { from: cFrom, to: cTo };
    case "today":
    default: return { from: today, to: today };
  }
};

// Per-area colour so the balanced revenue split reads at a glance.
const AREA_STYLE = {
  accommodation: { tint: "#E6F1FB", accent: "#185FA5" },
  fnb: { tint: "#E7F6EC", accent: "#0F6E56" },
  bar: { tint: "#FAECE7", accent: "#712B13" },
  roomservice: { tint: "#EAF3DE", accent: "#27500A" },
  wellness: { tint: "#EEEDFE", accent: "#3C3489" },
  events: { tint: "#FAEEDA", accent: "#854F0B" },
  other: { tint: "#eef2f6", accent: "#5b6472" },
};

export default function BusinessOverviewPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const money = (v) => formatPrice(v || 0, cur);

  const [preset, setPreset] = useState("month");
  const [customFrom, setCustomFrom] = useState(iso(0));
  const [customTo, setCustomTo] = useState(iso(0));
  const { from, to } = presetRange(preset, customFrom, customTo);
  const rangeLabel = from === to ? from : `${from} – ${to}`;

  // Feature gates — hide (and skip fetching) modules this tenant doesn't have.
  const showRooms = hasFeature("front_desk");
  const showWellness = hasFeature("services");
  const showFnb = getVenueType() !== "spa_gym";   // gym/spa venues don't run F&B

  // Each section: null = loading, ERR sentinel = failed (show fallback), else data.
  const ERR = { __error: true };
  const [z, setZ] = useState(null);       // revenue by area (z-report)
  const [rooms, setRooms] = useState(null);
  const [fnb, setFnb] = useState(null);
  const [wellness, setWellness] = useState(null);

  useEffect(() => {
    setZ(null); setRooms(null); setFnb(null); setWellness(null);
    // Fire in parallel; each catches its own failure so one gated endpoint
    // (role / feature) never blanks the whole page. Rooms & Wellness are only
    // fetched when the tenant actually has the module (avoids needless 403s).
    fetchFinanceZReport(undefined, { from, to }).then(setZ).catch(() => setZ(ERR));
    if (showRooms) fetchRoomsReport(from, to).then(setRooms).catch(() => setRooms(ERR));
    if (showFnb) fetchSummary({ start: from, end: to }).then(setFnb).catch(() => setFnb(ERR));
    if (showWellness) fetchServicesSummary().then(setWellness).catch(() => setWellness(ERR));
    /* eslint-disable-next-line */
  }, [from, to]);

  const isLoading = (x) => x == null;
  const isError = (x) => x && x.__error;

  // ---- reusable KPI card (mirrors the Metric component in RoomsReportPage) --
  const Metric = ({ label, value, sub, tint, accent }) => (
    <div style={{ background: tint, borderRadius: 14, padding: "16px 18px" }}>
      <div style={{ fontSize: 13, color: "#5f6b78" }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: accent, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#5f6b78", marginTop: 3 }}>{sub}</div>}
    </div>
  );

  // Section frame with title + shared loading / error handling.
  const Section = ({ title, state, children, emptyNote }) => (
    <div style={{ marginTop: 22 }}>
      <h2 style={{ fontSize: 18, margin: "0 0 10px" }}>{title}</h2>
      {isLoading(state) ? (
        <p className="muted" style={{ margin: 0 }}>…</p>
      ) : isError(state) ? (
        <div style={{ ...CARD, padding: 16 }} className="muted">{emptyNote || t("unavailable")}</div>
      ) : (
        children
      )}
    </div>
  );

  const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 };

  // ---- Section 1 data: revenue by area -------------------------------------
  const areaLabel = (key) => {
    const norm = String(key || "").toLowerCase().replace(/[\s/_-]/g, "");
    const map = { accommodation: "a_accommodation", rooms: "a_accommodation", fnb: "a_fnb", bar: "a_bar", roomservice: "a_roomservice", wellness: "a_wellness", spa: "a_wellness", events: "a_events", other: "a_other" };
    const tk = map[norm];
    return tk ? t(tk) : String(key || t("a_other"));
  };
  const styleFor = (key) => {
    const norm = String(key || "").toLowerCase().replace(/[\s/_-]/g, "");
    const canon = { accommodation: "accommodation", rooms: "accommodation", fnb: "fnb", bar: "bar", roomservice: "roomservice", wellness: "wellness", spa: "wellness", events: "events" }[norm] || "other";
    return AREA_STYLE[canon] || AREA_STYLE.other;
  };
  const buildAreas = () => {
    if (!z || isError(z)) return { total: 0, rows: [] };
    const total = Number((z.totals && z.totals.gross) || 0);
    const rows = (z.by_revenue_point || [])
      .map((p) => ({ key: p.point, label: areaLabel(p.point), amount: Number(p.gross || 0) }))
      .filter((r) => r.amount > 0)
      .sort((a, b) => b.amount - a.amount);
    return { total, rows };
  };
  const { total: revTotal, rows: areaRows } = buildAreas();

  return (
    <div style={{ maxWidth: 1040, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 2 }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      {/* Timeframe selector */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 14 }}>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("timeframe")}{" "}
          <select style={{ padding: 8, borderRadius: 8, border: "1px solid #d8dee2", marginLeft: 4 }} value={preset} onChange={(e) => setPreset(e.target.value)}>
            <option value="today">{t("tf_today")}</option>
            <option value="week">{t("tf_week")}</option>
            <option value="month">{t("tf_month")}</option>
            <option value="year">{t("tf_year")}</option>
            <option value="custom">{t("tf_custom")}</option>
          </select>
        </label>
        {preset === "custom" && (
          <>
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("from_l")}{" "}
              <input type="date" style={{ padding: 8, borderRadius: 8, border: "1px solid #d8dee2", marginLeft: 4 }} value={customFrom} max={customTo} onChange={(e) => setCustomFrom(e.target.value)} />
            </label>
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("to_l")}{" "}
              <input type="date" style={{ padding: 8, borderRadius: 8, border: "1px solid #d8dee2", marginLeft: 4 }} value={customTo} min={customFrom} onChange={(e) => setCustomTo(e.target.value)} />
            </label>
          </>
        )}
        <span style={{ fontSize: 12.5, color: "#5f6b78" }}>{t("range_l")}: <strong style={{ color: "#0F6E56" }}>{rangeLabel}</strong></span>
      </div>

      {/* ---------------- Section 1 — Revenue by area (headline) ------------- */}
      <Section title={t("rev_by_area")} state={z} emptyNote={t("unavailable")}>
        <div style={{ ...CARD, padding: "18px 20px" }}>
          <div style={{ fontSize: 13, color: "#5f6b78" }}>{t("total_revenue")}</div>
          <div style={{ fontSize: 34, fontWeight: 800, color: "#0F6E56", margin: "2px 0 14px" }}>{money(revTotal)}</div>
          {areaRows.length === 0 ? (
            <div className="muted" style={{ fontSize: 13 }}>{t("no_revenue")}</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {areaRows.map((r) => {
                const pct = revTotal > 0 ? (r.amount / revTotal) * 100 : 0;
                const st = styleFor(r.key);
                return (
                  <div key={r.key} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 150, fontSize: 13.5, color: "#3a4652", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.label}</div>
                    <div style={{ flex: 1, background: "#eef2f6", borderRadius: 6, height: 20, overflow: "hidden" }}>
                      <div style={{ width: `${Math.max(2, pct)}%`, background: st.accent, height: "100%", borderRadius: 6 }} />
                    </div>
                    <div style={{ width: 130, textAlign: "right", fontSize: 13.5, fontWeight: 700 }}>{money(r.amount)}</div>
                    <div style={{ width: 96, textAlign: "right", fontSize: 12.5, color: "#5f6b78" }}>{pct.toFixed(1)}% {t("pct_of_total")}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Section>

      {/* ---------------- Section 2 — Rooms (front-desk feature only) -------- */}
      {showRooms && (
      <Section title={t("rooms")} state={rooms}>
        {!isLoading(rooms) && !isError(rooms) && (
          <div style={grid}>
            <Metric label={t("occupancy")} value={rooms.occupancy_pct == null ? "—" : `${rooms.occupancy_pct}%`} tint="#FAEEDA" accent="#854F0B" />
            <Metric label={t("adr")} value={rooms.adr == null ? "—" : money(rooms.adr)} tint="#EEEDFE" accent="#3C3489" />
            <Metric label={t("revpar")} value={rooms.revpar == null ? "—" : money(rooms.revpar)} tint="#FAECE7" accent="#712B13" />
            <Metric label={t("room_revenue")} value={money(rooms.revenue)} tint="#E7F6EC" accent="#0F6E56" />
            <Metric label={t("room_nights")} value={rooms.room_nights == null ? "—" : rooms.room_nights} tint="#EAF3DE" accent="#27500A" />
            <Metric label={t("bookings")} value={rooms.bookings == null ? "—" : rooms.bookings} tint="#E6F1FB" accent="#185FA5" />
          </div>
        )}
      </Section>
      )}

      {/* ---------------- Section 3 — Food & Beverage ----------------------- */}
      {showFnb && (
      <Section title={t("fnb")} state={fnb}>
        {!isLoading(fnb) && !isError(fnb) && (
          <div style={grid}>
            <Metric label={t("fnb_revenue")} value={money(fnb.revenue)} tint="#E7F6EC" accent="#0F6E56" />
            <Metric label={t("orders")} value={fnb.total_orders == null ? "—" : fnb.total_orders} tint="#E6F1FB" accent="#185FA5" />
            <Metric label={t("items_sold")} value={fnb.total_items_sold == null ? "—" : fnb.total_items_sold} tint="#EAF3DE" accent="#27500A" />
            <Metric label={t("avg_order")} value={money(fnb.average_order_value)} tint="#FAEEDA" accent="#854F0B" />
          </div>
        )}
      </Section>
      )}

      {/* ---------------- Section 4 — Wellness (services feature only) ------ */}
      {showWellness && (
      <Section title={t("wellness")} state={wellness}>
        {!isLoading(wellness) && !isError(wellness) && (
          <div style={grid}>
            <Metric label={t("service_revenue")} value={money(wellness.services_revenue)} tint="#EEEDFE" accent="#3C3489" />
            <Metric label={t("membership_dues")} value={money(wellness.membership_dues)} tint="#E7F6EC" accent="#0F6E56" />
            <Metric label={t("active_members")} value={wellness.active_members == null ? "—" : wellness.active_members} tint="#E6F1FB" accent="#185FA5" />
            <Metric label={t("service_bookings")} value={wellness.bookings_total == null ? "—" : wellness.bookings_total} tint="#FAEEDA" accent="#854F0B" />
          </div>
        )}
      </Section>
      )}
    </div>
  );
}
